interface DeviceCapabilities {
  numberOfWorkers: number;
  canHandleHiRes: boolean;
  maxConcurrentBlobs: number;
  preferredQuality: 'high' | 'medium' | 'low';
  batchSize: number;
  reason?: string;
}

interface DeviceSpecs {
  cores: number;
  memory: number; // GB
  isLowEnd: boolean;
  isMobile: boolean;
  browserEngine: string;
  supportsOffscreenCanvas: boolean;
  supportsWebCodecs: boolean;
}

function getDeviceSpecs(): DeviceSpecs {
  const cores = navigator.hardwareConcurrency || 4;

  // Estimate memory based on various signals
  const memory = (() => {
    // eslint-disable-next-line
    // @ts-ignore - deviceMemory is experimental
    if (navigator.deviceMemory) {
      // eslint-disable-next-line
      // @ts-ignore
      return navigator.deviceMemory;
    }

    // Fallback estimation based on cores and other signals
    if (cores <= 2) return 2; // Likely low-end device
    if (cores <= 4) return 4; // Mid-range
    if (cores <= 8) return 8; // High-end
    return 16; // Very high-end
  })();

  // Detect if device is likely low-end
  const isLowEnd = memory <= 2 || cores <= 2;

  // Detect mobile
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

  // Browser engine detection
  const browserEngine = (() => {
    if (navigator.userAgent.includes('Chrome')) return 'blink';
    if (navigator.userAgent.includes('Firefox')) return 'gecko';
    if (navigator.userAgent.includes('Safari')) return 'webkit';
    return 'unknown';
  })();

  // Feature detection
  const supportsOffscreenCanvas = typeof OffscreenCanvas !== 'undefined';
  const supportsWebCodecs = 'VideoDecoder' in window;

  return {
    cores,
    memory,
    isLowEnd,
    isMobile,
    browserEngine,
    supportsOffscreenCanvas,
    supportsWebCodecs,
  };
}

export function deviceCompatibility(
  clipCount = 1,
  tourDuration = 60 // seconds
): DeviceCapabilities {
  const specs = getDeviceSpecs();

  // Calculate base worker count
  let numberOfWorkers = Math.max(1, Math.floor(specs.cores / 2) + 1);

  // Limit workers based on device capabilities and clip count
  numberOfWorkers = Math.min(numberOfWorkers, clipCount, 4); // Never more than 4 workers

  // Adjust for low-end devices
  if (specs.isLowEnd) {
    numberOfWorkers = Math.min(numberOfWorkers, 2);
  }

  // Mobile devices - be more conservative
  if (specs.isMobile) {
    numberOfWorkers = Math.min(numberOfWorkers, specs.memory <= 3 ? 1 : 2);
  }

  // Determine HiRes capability
  const canHandleHiRes = (() => {
    // Low-end devices: no HiRes
    if (specs.isLowEnd) return false;

    // Low memory mobile devices: no HiRes
    if (specs.isMobile && specs.memory <= 3) return false;

    // Short videos on decent devices: yes
    if (tourDuration <= 30 && specs.memory >= 4) return true;

    // Longer videos: need more memory
    if (tourDuration > 120 && specs.memory < 8) return false;

    // Default: yes for devices with 4GB+ RAM
    return specs.memory >= 4;
  })();

  // Calculate max concurrent blobs (estimate)
  const maxConcurrentBlobs = (() => {
    // Estimate ~50KB per JPEG blob for low-res, ~200KB for hi-res
    const avgBlobSize = canHandleHiRes ? 200 : 50; // KB
    const availableMemory = specs.memory * 1024 * 1024; // Convert to KB
    const memoryForBlobs = availableMemory * 0.1; // Use 10% of RAM for blobs

    return Math.floor(memoryForBlobs / avgBlobSize);
  })();

  // Preferred quality
  const preferredQuality: 'high' | 'medium' | 'low' = (() => {
    if (!canHandleHiRes) return 'low';
    if (specs.memory >= 8 && specs.cores >= 6) return 'high';
    return 'medium';
  })();

  // Batch size for frame processing
  const batchSize = (() => {
    if (specs.isLowEnd) return 5; // Small batches
    if (specs.isMobile) return 10; // Medium batches
    return 20; // Large batches for desktop
  })();

  // Reason for debugging
  const reason = `${specs.cores}C/${specs.memory}GB RAM, ${
    specs.isMobile ? 'mobile' : 'desktop'
  }, ${specs.browserEngine}`;

  return {
    numberOfWorkers,
    canHandleHiRes,
    maxConcurrentBlobs,
    preferredQuality,
    batchSize,
    reason,
  };
}

// Performance monitoring utilities
export function monitorPerformance() {
  const startTime = performance.now();
  let frameCount = 0;

  return {
    recordFrame: () => {
      frameCount++;
    },

    getStats: () => {
      const elapsed = performance.now() - startTime;
      const fps = frameCount / (elapsed / 1000);

      return {
        framesProcessed: frameCount,
        elapsedTime: elapsed,
        averageFPS: fps,
        isPerformanceGood: fps > 15, // Arbitrary threshold
      };
    },
  };
}

// Helper to check if device needs performance adjustments
export function shouldReduceQuality(
  currentFrameRate: number,
  memoryUsage: number
): boolean {
  // Reduce quality if FPS drops below 10 or memory usage > 80%
  return currentFrameRate < 10 || memoryUsage > 0.8;
}
